import { Injectable, signal } from '@angular/core';
import { Photo } from '@capacitor/camera';
import dayjs from 'dayjs';
import { LocalStorageService } from 'ngx-webstorage';
import { delay, distinctUntilChanged, EMPTY, firstValueFrom, forkJoin, from, Observable, of, Subject, throwError } from 'rxjs';
import { concatMap, filter, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { CreneauType } from '../app.constants';
import { ChampCompteRendu, EtapeChampCompteRendu } from '../entities/compte-rendu/champ-compte-rendu.model';
import { Coordinates } from '../entities/coordinates.model';
import { ContactReponseDTO } from '../entities/creneaux/contact-reponse.dto';
import { CreneauGardiennage } from '../entities/creneaux/creneau-gardiennage.model';
import { CreneauInterPonctuelle } from '../entities/creneaux/creneau-inter-ponctuelle.model';
import { ICreneauInter } from '../entities/creneaux/creneau-inter.interface';
import { CreneauIntervention } from '../entities/creneaux/creneau-intervention.model';
import { CreneauTournee } from '../entities/creneaux/creneau-tournee.model';
import { ICreneau } from '../entities/creneaux/creneau.interface';
import { ProchainsCreneaux } from '../entities/creneaux/prochains-creneau.model';
import { Zone } from '../entities/creneaux/zone.model';
import { IPrecisionIntervention } from '../entities/demande/precision-intervention';
import { INommable } from '../entities/nommable.interface';
import { AgentVaAppelerClientPourAccesSiteEvent } from '../entities/user-events/agent-va-appeler-client.event';
import { AnomalieRondePonctuelleEvent } from '../entities/user-events/anomalie-ronde-ponctuelle.event';
import { AnomalieRondeEvent } from '../entities/user-events/anomalie-ronde.event';
import { AnomalieZoneEvent } from '../entities/user-events/anomalie-zone.event';
import { AppelClientEvent } from '../entities/user-events/appel-client.event';
import { ArriveeSurSiteEvent } from '../entities/user-events/arrivee-sur-site.event';
import { CompteRenduEvent } from '../entities/user-events/compte-rendu.event';
import { DebutVacationGardiennageEvent } from '../entities/user-events/debut-vacation-gardiennage.event';
import { DebutVacationInterPonctuelle } from '../entities/user-events/debut-vacation-inter-ponctuelle.event';
import { DebutVacationInterventionEvent } from '../entities/user-events/debut-vacation-intervention.event';
import { DebutVacationTourneeEvent } from '../entities/user-events/debut-vacation-tournee.event';
import { FinVacationGardiennageEvent } from '../entities/user-events/fin-vacation-gardiennage.event';
import { FinVacationInterPonctuelleEvent } from '../entities/user-events/fin-vacation-inter-ponctuelle.event';
import { FinVacationInterventionEvent } from '../entities/user-events/fin-vacation-intervention.event';
import { FinVacationTourneeEvent } from '../entities/user-events/fin-vacation-tournee.event';
import { ClotureCompteRenduPreliminaireEvent } from '../entities/user-events/finish-vacation-inter-ponctuelle.event';
import { InfoRondePonctuelleEvent } from '../entities/user-events/info-ronde-ponctuelle.event';
import { InfoRondeEvent } from '../entities/user-events/info-ronde.event';
import { PointageRondePonctuelleEvent } from '../entities/user-events/pointage-ronde-ponctuelle.event';
import { PointageRondeEvent } from '../entities/user-events/pointage-ronde.event';
import { SetContactReponseEvent } from '../entities/user-events/set-contact-reponse.event';
import { UUID } from '../entities/uuid.model';
import { VacationGardiennage } from '../entities/vacation/vacation-gardiennage.model';
import { VacationInterventionPonctuelle } from '../entities/vacation/vacation-intervention-ponctuelle.model';
import { VacationIntervention } from '../entities/vacation/vacation-intervention.model';
import { VacationTournee } from '../entities/vacation/vacation-tournee.model';
import { VerifMateriel } from '../entities/verif-materiel.model';
import { AucunCreneauCourantException } from '../shared/exceptions/aucun-creneau-courant.exception';
import { InvalidActionException } from '../shared/exceptions/invalid-action.exception';
import { LoggingService } from '../shared/logging.service';
import { PhotoEncoderService } from '../shared/photo-encoder.service';
import { GeolocationService } from './geolocation.service';
import { ProchainsCreneauxReadService } from './prochains-creneaux.read.service';
import { UserEventWriteService } from './user-event.write.service';
import { ZonesReadService } from './zones.read.service';

@Injectable({
  providedIn: 'root',
})
export class CurrentCreneauService {
  private _isUpdating$ = new Subject<boolean>();
  private readonly tout = signal<ProchainsCreneaux>(new ProchainsCreneaux(), { equal: () => false });
  private readonly computedHasConsigne = signal(false);
  private readonly chosenNextSubCreneauId = signal<string | null>(null);
  private readonly currentSubCreneau = signal<ICreneau | null>(null);
  private readonly computedHasMainCourante = signal(false);
  private readonly isStartingInterventionNonPlannifiee = signal(false);
  private readonly creneauInterventionNonPlannifiee = signal<CreneauIntervention | undefined>(undefined);
  private readonly zones = signal<Zone[]>([], { equal: () => false });
  private readonly computedNbInterPonctuelles = signal(0);
  private readonly computedIsCompteRenduRemplis = signal(false);
  private readonly _isUpdating = signal(false);
  private latestRefresh = 0;
  private readonly precisionsLues = signal<string[]>([], { equal: () => false });
  private readonly STORAGE_KEY_PRECISIONS_LUES = 'precisions_lues';
  private readonly STORAGE_KEY_CURRENT_CRENEAU_ID = 'current_creneau_id';
  private readonly DELAY_ATTENTE_FIN_INTER_IN_SECONDS = 300;

  constructor(
    private readonly userEventService: UserEventWriteService,
    private readonly creneauxReadService: ProchainsCreneauxReadService,
    private readonly geolocationService: GeolocationService,
    private readonly photoEncoder: PhotoEncoderService,
    private readonly zonesReadService: ZonesReadService,
    private readonly log: LoggingService,
    private readonly localStorage: LocalStorageService
  ) {
    this.loadPrecisionsLues();
  }

  private loadPrecisionsLues() {
    try {
      const value = this.localStorage.retrieve(this.STORAGE_KEY_PRECISIONS_LUES);
      const currentCreneauId = this.localStorage.retrieve(this.STORAGE_KEY_CURRENT_CRENEAU_ID);

      // Si on a des précisions mais que le créneau a changé, on les nettoie
      if (value && this.tout()?.courant?.id && currentCreneauId !== this.tout()?.courant?.id) {
        this.precisionsLues.set([]);
        this.localStorage.clear(this.STORAGE_KEY_PRECISIONS_LUES);
        this.localStorage.clear(this.STORAGE_KEY_CURRENT_CRENEAU_ID);
      } else if (value) {
        this.precisionsLues.set(value);
      }
    } catch (error) {
      this.log.error('Erreur lors du chargement des précisions lues:', error);
    }
  }

  private savePrecisionsLues() {
    try {
      this.localStorage.store(this.STORAGE_KEY_PRECISIONS_LUES, this.precisionsLues());
      this.localStorage.store(this.STORAGE_KEY_CURRENT_CRENEAU_ID, this.tout()?.courant?.id);
    } catch (error) {
      this.log.error('Erreur lors de la sauvegarde des précisions lues:', error);
    }
  }

  public isInterventionImprevueGeolocalized(): boolean {
    return this.tout().parametresMobiles?.geolocOnInterEnabled ?? false;
  }

  public getTelDispatcher(): string | undefined {
    if (
      this.hasCurrentCreneau() &&
      this.getCurrentCreneauOrThrow().type !== CreneauType.GARDIENNAGE &&
      this.tout().parametresMobiles?.telDispatcherMobile
    ) {
      return this.tout().parametresMobiles?.telDispatcherMobile;
    } else {
      return this.tout().parametresMobiles?.telDispatcher;
    }
  }

  public hasCurrentCreneau(): boolean {
    return this.tout().courant != null;
  }

  public isVacationEnCours(): boolean {
    const vacation = this.tout().courant?.vacation;
    return vacation != null && vacation.debut != null && vacation.fin == null;
  }

  public isWaitingBeforeLeaving(): boolean {
    const courant = this.tout().courant;
    const vacation = courant?.vacation;
    return courant?.type === CreneauType.INTER_PONCTUELLE && vacation != null && vacation.debut != null && vacation.fin != null;
  }

  public async getWaitingEndTime(): Promise<dayjs.Dayjs> {
    return dayjs(this.getCurrentCreneauOrThrow().vacation?.fin).add(this.DELAY_ATTENTE_FIN_INTER_IN_SECONDS, 'seconds');
  }

  public hasConsigne(): boolean {
    return this.computedHasConsigne();
  }

  public hasPrecisionsNonLues(): boolean {
    return this.getPrecisionsNonLues().length > 0;
  }

  public getPrecisionsNonLues(): IPrecisionIntervention[] {
    return (this.getCurrentCreneau() as ICreneauInter)?.precisions?.filter(p => !this.precisionsLues().includes(p.id)) ?? [];
  }

  public hasMainCourante(): boolean {
    return this.computedHasMainCourante();
  }

  public getCurrentCreneau(): ICreneau | null {
    return this.tout().courant;
  }

  public getCurrentCreneauOrThrow(): ICreneau {
    const courant = this.tout().courant;
    if (!courant) {
      throw new AucunCreneauCourantException();
    }
    return courant;
  }

  public getCurrentSubCreneau(): ICreneau | null {
    return this.currentSubCreneau();
  }

  public isSubVacationEnCours(): boolean {
    return !!this.currentSubCreneau();
  }

  public getCurrentSubCreneauOrCreneau(): ICreneau | null {
    return this.currentSubCreneau() ?? this.tout().courant;
  }

  public canScanPointeau(): boolean {
    return this.isVacationEnCours();
    // &&
    // (
    //   // ronde pointeaux
    //   (this.getCurrentCreneau().type === CreneauType.TOURNEE && (this.getCurrentCreneau() as CreneauTournee).rondes.filter(r => r.type === CreneauType.RONDE_POINTEAUX).length > 0)
    //   ||
    //   // gardiennage pointeaux
    //   (this.getCurrentCreneau().type === CreneauType.GARDIENNAGE && (this.getCurrentCreneau() as CreneauGardiennage).parcours.length > 0)
    // )
  }

  public chooseNextSubCreneau(idCreneau: string): void {
    this.chosenNextSubCreneauId.set(idCreneau);
  }

  public cancelInterventionPreparation(): void {
    this.creneauInterventionNonPlannifiee.set(undefined);
    this.updateComputedValues();
  }

  public setCreneauIntervention(creneau: CreneauIntervention): void {
    this.creneauInterventionNonPlannifiee.set(creneau);
    this.updateComputedValues();
  }

  public isInterventionNext(): boolean {
    return this.isStartingInterventionNonPlannifiee();
  }

  public getCreneauIntervention(): CreneauIntervention | undefined {
    return this.creneauInterventionNonPlannifiee();
  }

  public isCompteRenduRemplis(): boolean {
    return this.computedIsCompteRenduRemplis();
  }

  public getNextSubCreneau(): ICreneau {
    if (this.chosenNextSubCreneauId()) {
      const ronde = (this.getCurrentCreneauOrThrow() as CreneauTournee).rondes.find(r => r.id === this.chosenNextSubCreneauId());
      if (!ronde) {
        throw new InvalidActionException('Ronde choisie introuvable');
      }
      return ronde;
    } else {
      throw new Error('Next sub-creneau not chosen.');
    }
  }

  public isItTimeToStartCurrentCreneau(): boolean {
    const delaiAvance = this.tout().parametresMobiles?.delaiVacationAvanceMinutes;
    if (delaiAvance) {
      let acceptableEarlyStart = dayjs(this.getCurrentCreneauOrThrow().debut);
      acceptableEarlyStart = acceptableEarlyStart.subtract(delaiAvance, 'minutes');
      return dayjs().isAfter(acceptableEarlyStart);
    } else {
      return true;
    }
  }

  public isCurrentCreneauInThePast(): boolean {
    if (this.tout().courant?.fin) {
      return dayjs().isAfter(dayjs(this.getCurrentCreneauOrThrow().fin));
    } else {
      return false;
    }
  }

  public skipCurrentCreneauAndResfresh(): Observable<void> {
    this.tout().courant = null;
    return this.updateState(this.tout()).pipe(
      switchMap(() => this.refreshCreneau()),
      switchMap(() => of(void 0))
    );
  }

  public getMaximumEarlyStartInMinutes(): number {
    return this.tout().parametresMobiles?.delaiVacationAvanceMinutes ?? 0;
  }

  public saveCache(): Observable<any> {
    return of(this.creneauxReadService.saveLocally(this.tout()));
  }

  public refreshCreneau(): Observable<ProchainsCreneaux> {
    const id = ++this.latestRefresh;
    this.setIsUpdating(true);
    return forkJoin([this.zonesReadService.fetch(), this.creneauxReadService.fetch()]).pipe(
      concatMap(([zonesLues, prochainsLus]) => {
        if (id !== this.latestRefresh) {
          // Un refresh plus récent a pris la main : on n'écrase pas l'état avec une lecture potentiellement périmée,
          // et on attend qu'il aboutisse pour ne pas rendre la main (et couper le loader) avec l'ancien état.
          this.log.info(`Rafraîchissement des créneaux ${id} supplanté par le ${this.latestRefresh} : attente de ce dernier`);
          return this.isUpdating()
            ? this.isUpdating$().pipe(
                filter(enCours => !enCours),
                take(1)
              )
            : of(undefined);
        }
        if (prochainsLus) {
          this.zones.set(zonesLues ?? []);
          this.updateState(prochainsLus);
        }
        this.setIsUpdating(false);
        return of(undefined);
      }),
      map(() => this.tout()),
      finalize(() => {
        if (id === this.latestRefresh) {
          this.setIsUpdating(false);
        }
      })
    );
  }

  public loadCurrentCreneau(): Promise<ICreneau | null> {
    if ((this.currentSubCreneau() ?? this.tout().courant) && this.zones().length) {
      return Promise.resolve(this.currentSubCreneau() ?? this.tout().courant);
    }
    return firstValueFrom(this.refreshCreneau()).then(() => this.currentSubCreneau() ?? this.tout().courant);
  }

  public async findZone(zoneId: UUID): Promise<Zone | null> {
    const current = await this.loadCurrentCreneau();

    if (current instanceof CreneauTournee) {
      const zone = (current as CreneauTournee).zones.find(z => z.id === zoneId);
      if (zone) {
        return zone;
      }
    }
    if (current instanceof CreneauIntervention) {
      if (current.site?.zone?.id === zoneId) {
        return current.site.zone;
      }
    }
    if (this.tout().courant instanceof CreneauTournee) {
      const zone = (this.tout().courant as CreneauTournee).zones.find(z => z.id === zoneId);
      if (zone) {
        return zone;
      }
    }
    if (this.zones()) {
      const zone = this.zones().find(z => z.id === zoneId);
      if (zone) {
        return zone;
      }
    } else {
      this.log.warn('zones not loaded');
    }
    return null;
  }

  /**
   * Mets à jour l'état interne et sauvegarde
   */
  public updateState(tout: ProchainsCreneaux): Observable<any> {
    // Si aucun creneau n'est en cours, on cherche lequel est le prochain pour le mettre dans "courant"
    const creneauxNonPonctuels = tout.prochains.filter(creneau => creneau.type !== CreneauType.INTER_PONCTUELLE);
    if (!tout.courant && creneauxNonPonctuels.length > 0) {
      tout.courant = creneauxNonPonctuels[0];
      tout.prochains.splice(tout.prochains.indexOf(tout.courant), 1);
    }

    const ancienCourant = this.tout().courant;
    if (ancienCourant && !tout.courant) {
      this.log.warn(`Créneau courant ${ancienCourant.id} perdu au rafraîchissement (${tout.prochains?.length ?? 0} prochain(s) reçu(s))`);
    }

    this.tout.set(tout);
    this.updateComputedValues();
    return of(
      this.creneauxReadService
        .saveLocally(tout)
        .catch(err =>
          this.log.warn("Mise en cache des créneaux impossible : l'état sera relu depuis le serveur au prochain démarrage.", err)
        )
    );
  }

  public nouveauBonDeRonde(
    typeDeBon: 'pointage' | 'information' | 'anomalie',
    passageTime: dayjs.Dayjs,
    pointeau?: INommable,
    site?: INommable,
    zone?: INommable,
    texte?: string,
    photos?: Photo[],
    signature?: string,
    nouveauSite?: INommable,
    typePrestation?: string
  ): Observable<any> {
    return this.getCoordsObsIf(this.tout().courant?.geolocEnabled ?? true).pipe(
      concatMap(coords => from(this.photoEncoder.encoder(photos)).pipe(map(encodedPhotos => ({ coords, encodedPhotos })))),
      switchMap(({ coords, encodedPhotos }) => {
        const observables: Observable<any>[] = [];
        if (zone && typeDeBon === 'anomalie') {
          observables.push(
            this.userEventService.put(
              new AnomalieZoneEvent(this.getCurrentCreneau()?.vacation?.id, zone.id, texte, encodedPhotos, passageTime, coords)
            )
          );
        } else {
          if (pointeau || typeDeBon === 'pointage') {
            observables.push(
              this.userEventService.put(
                typePrestation === 'RONDE_PONCTUELLE'
                  ? new PointageRondePonctuelleEvent(
                      passageTime,
                      pointeau?.id,
                      this.getCurrentCreneau()?.id,
                      coords,
                      undefined,
                      site?.id,
                      zone?.id,
                      nouveauSite
                    )
                  : new PointageRondeEvent(
                      passageTime,
                      pointeau?.id,
                      this.getCurrentCreneau()?.id,
                      coords,
                      undefined,
                      site?.id,
                      zone?.id,
                      nouveauSite
                    )
              )
            );
          }

          if (typeDeBon === 'information') {
            observables.push(
              this.userEventService.put(
                typePrestation === 'RONDE_PONCTUELLE'
                  ? new InfoRondePonctuelleEvent(
                      texte,
                      encodedPhotos,
                      passageTime,
                      coords,
                      site?.id,
                      this.getCurrentCreneau()?.id,
                      nouveauSite
                    )
                  : new InfoRondeEvent(texte, encodedPhotos, passageTime, coords, site?.id, this.getCurrentCreneau()?.id, nouveauSite)
              )
            );
          } else if (typeDeBon === 'anomalie') {
            observables.push(
              this.userEventService.put(
                typePrestation === 'RONDE_PONCTUELLE'
                  ? new AnomalieRondePonctuelleEvent(
                      texte,
                      encodedPhotos,
                      passageTime,
                      coords,
                      signature,
                      zone?.id,
                      site?.id,
                      this.getCurrentCreneau()?.id,
                      nouveauSite
                    )
                  : new AnomalieRondeEvent(
                      texte,
                      encodedPhotos,
                      passageTime,
                      coords,
                      signature,
                      zone?.id,
                      site?.id,
                      this.getCurrentCreneau()?.id,
                      nouveauSite
                    )
              )
            );
          }
        }

        if (observables.length === 0) {
          return throwError(
            () =>
              new Error(
                `Bon de ronde incomplet, aucun évènement à envoyer (type ${typeDeBon}, zone ${zone?.id}, ` +
                  `pointeau ${pointeau?.id}, prestation ${typePrestation})`
              )
          );
        }
        return forkJoin(observables);
      }),
      switchMap(() => this.refreshCreneau())
    );
  }

  public startNextVacationGardiennage(
    materiel?: VerifMateriel,
    champsDebut: ChampCompteRendu[] = [],
    consignesLues: string[] = []
  ): Observable<any> {
    const now = dayjs();
    const uuidGen = uuidv4();

    this.getCurrentCreneauOrThrow().vacation = new VacationGardiennage(uuidGen, now, undefined, undefined);

    return this.updateState(this.tout()).pipe(
      switchMap(() => this.getCoordsObsIf(this.getCurrentCreneauOrThrow().geolocEnabled ?? true)),
      switchMap(coords =>
        this.userEventService.put(
          new DebutVacationGardiennageEvent(this.getCurrentCreneauOrThrow().id, uuidGen, now, materiel, champsDebut, consignesLues, coords)
        )
      ),
      switchMap(() => this.refreshCreneau())
    );
  }

  public startNextVacationTournee(
    materiel?: VerifMateriel,
    champsDebut: ChampCompteRendu[] = [],
    consignesLues: string[] = []
  ): Observable<any> {
    const now = dayjs();
    const uuidGen = uuidv4();

    this.getCurrentCreneauOrThrow().vacation = new VacationTournee(now, undefined, uuidGen);

    return this.updateState(this.tout()).pipe(
      switchMap(() => this.getCoordsObsIf(this.getCurrentCreneauOrThrow().geolocEnabled ?? true)),
      switchMap(coords =>
        this.userEventService.put(
          new DebutVacationTourneeEvent(this.getCurrentCreneauOrThrow().id, uuidGen, now, materiel, champsDebut, consignesLues, coords)
        )
      ),
      switchMap(() => this.refreshCreneau())
    );
  }

  public startVacationInterPonctuelle(prestationId: string): Observable<any> {
    const now = dayjs();
    const uuidGen = uuidv4();

    const demande = this.getAllIntersPonctuelles().find(dem => dem.prestationId === prestationId);

    if (!demande) {
      console.warn('Demande introuvable', prestationId);
      return EMPTY;
    }

    this.tout().courant = demande;

    this.getCurrentCreneauOrThrow().vacation = new VacationInterventionPonctuelle(now, undefined, uuidGen);

    return this.updateState(this.tout()).pipe(
      switchMap(() => this.getCoordsObsIf(this.getCurrentCreneauOrThrow().geolocEnabled ?? true)),
      switchMap(coords =>
        this.userEventService.put(
          new DebutVacationInterPonctuelle(this.getCurrentCreneauOrThrow().id, uuidGen, now, undefined, undefined, undefined, coords)
        )
      ),
      switchMap(() => this.refreshCreneau())
    );
  }

  public startVacationIntervention(
    site: INommable,
    materiel?: VerifMateriel,
    champsDebut: ChampCompteRendu[] = [],
    consignesLues: string[] = []
  ): Observable<any> {
    const now = dayjs();
    const uuidGen = uuidv4();
    let idTournee: string;
    const creneauInter: CreneauIntervention = this.getCreneauInterWithVacation(uuidGen, now, site);

    if (this.tout().courant) {
      // Tournée en cours ou prochain créneau
      if (this.getCurrentCreneauOrThrow().type === CreneauType.TOURNEE && this.getCurrentCreneauOrThrow().vacation) {
        // Cas d'une inter pendant une tournée
        idTournee = this.getCurrentCreneauOrThrow().id;
        (this.tout().courant as CreneauTournee).inters.push(creneauInter);
      } else {
        // Si ce n'est pas une tournée  en cours, on doit remettre le créneau courant dans la liste des prochains
        this.tout().prochains = [this.getCurrentCreneauOrThrow(), ...this.tout().prochains];
        this.tout().courant = creneauInter;
      }
    } else {
      // Aucun créneau à venir
      this.tout().courant = creneauInter;
    }

    return this.updateState(this.tout()).pipe(
      switchMap(() => this.getCoordsObsIf(creneauInter.geolocEnabled)),
      switchMap(coords =>
        this.userEventService.put(
          new DebutVacationInterventionEvent(creneauInter.id, uuidGen, now, site, idTournee, materiel, champsDebut, consignesLues, coords)
        )
      ),
      tap(() => this.creneauInterventionNonPlannifiee.set(undefined)),
      switchMap(() => this.refreshCreneau())
    );
  }

  public finishInterPonctuelle(): Observable<any> {
    return this.updateState(this.tout()).pipe(
      switchMap(() => this.getCoordsObsIf(this.getCurrentCreneauOrThrow().geolocEnabled ?? true)),
      switchMap(coords =>
        this.userEventService.put(new ClotureCompteRenduPreliminaireEvent(this.getCurrentCreneauOrThrow().id, dayjs(), coords))
      ),
      switchMap(() => this.refreshCreneau())
    );
  }

  public appelClient(): Observable<any> {
    return this.updateState(this.tout()).pipe(
      switchMap(() => this.getCoordsObsIf(this.getCurrentCreneauOrThrow().geolocEnabled ?? true)),
      delay(10000), // 10 secondes de retard pour pas que l'évènement "l'agent a tenté de vous appeler" arrive sur Seculib B2CM avant que le client reçoive un appel
      switchMap(coords => this.userEventService.put(new AppelClientEvent(this.getCurrentCreneauOrThrow().id, dayjs(), coords))),
      switchMap(() => this.refreshCreneau())
    );
  }

  /**
   * Différent de "appelClient", qui représente un "appel passé"
   */
  public vaAppelerClientPourAccesSiteEvent(): Observable<any> {
    return this.updateState(this.tout()).pipe(
      switchMap(() => this.getCoordsObsIf(this.getCurrentCreneauOrThrow().geolocEnabled ?? true)),
      switchMap(coords =>
        this.userEventService.put(new AgentVaAppelerClientPourAccesSiteEvent(this.getCurrentCreneauOrThrow().id, coords))
      ),
      switchMap(() => this.refreshCreneau())
    );
  }

  public appelClientOnPresta(id: string): Observable<any> {
    return this.updateState(this.tout()).pipe(
      switchMap(() => this.getCoordsObsIf(this.getCurrentCreneauOrThrow().geolocEnabled ?? true)),
      delay(10000), // 10 secondes de retard
      switchMap(coords => this.userEventService.put(new AppelClientEvent(id, dayjs(), coords))),
      switchMap(() => this.refreshCreneau())
    );
  }

  public setContactReponse(contactReponse: ContactReponseDTO): Observable<any> {
    return this.updateState(this.tout()).pipe(
      switchMap(() => this.getCoordsObsIf(this.getCurrentCreneauOrThrow().geolocEnabled ?? true)),
      switchMap(coords =>
        this.userEventService.put(new SetContactReponseEvent(this.getCurrentCreneauOrThrow().id, dayjs(), coords, contactReponse))
      ),
      switchMap(() => this.refreshCreneau())
    );
  }

  public declareArriveeSurSite(champsPersos: ChampCompteRendu[]): Observable<any> {
    const uuidGen = uuidv4();
    const creneau = (this.currentSubCreneau() ?? this.getCurrentCreneauOrThrow()) as CreneauGardiennage | CreneauInterPonctuelle;
    const vacation = creneau.vacation;
    if (!vacation) {
      throw new AucunCreneauCourantException('Aucune vacation en cours');
    }

    vacation.arriveeSurSite = dayjs();

    return this.updateState(this.tout()).pipe(
      switchMap(() => this.getCoordsObsIf(creneau.geolocEnabled ?? true)),
      switchMap(coords =>
        this.userEventService.put(
          new ArriveeSurSiteEvent(creneau.id, uuidGen, vacation.arriveeSurSite as dayjs.Dayjs, champsPersos, coords)
        )
      ),
      // tap(() => this.creneauInterventionNonPlannifiee.set(undefined),
      switchMap(() => this.refreshCreneau())
    );
  }

  public saveCompteRendu(champs: ChampCompteRendu[]): Observable<any> {
    const creneau = this.tout().courant as CreneauInterPonctuelle;

    creneau.champsPersonnalises = champs;

    return this.updateState(this.tout()).pipe(
      switchMap(() => this.getCoordsObsIf(creneau.geolocEnabled)),
      switchMap(coords => this.userEventService.put(new CompteRenduEvent(creneau.id, champs, coords))),
      switchMap(() => this.refreshCreneau())
    );
  }

  private getCreneauInterWithVacation(uuidGen: any, now: dayjs.Dayjs, site: INommable): CreneauIntervention {
    const creneauNonPlannifie = this.creneauInterventionNonPlannifiee();
    if (!creneauNonPlannifie) {
      throw new AucunCreneauCourantException('Aucune intervention non planifiée en préparation');
    }
    const creneau = CreneauIntervention.hydrate(creneauNonPlannifie);
    creneau.vacation = new VacationIntervention(uuidGen, now, undefined, undefined, [], [], site);
    return creneau;
  }

  public endCurrentVacation(): Observable<any> {
    const type = this.getCurrentCreneauOrThrow().type;
    if (type === CreneauType.GARDIENNAGE) {
      return this.endCurrentVacationGardiennage(null);
    } else if (type === CreneauType.INTERVENTION) {
      return this.endCurrentVacationIntervention(null);
    } else if (type === CreneauType.INTER_PONCTUELLE) {
      return this.endCurrentVacationInterPonctuelle(null);
    } else if (type === CreneauType.TOURNEE) {
      if (this.currentSubCreneau()) {
        return this.endCurrentVacationIntervention(null);
      } else {
        return this.endCurrentVacationTournee();
      }
    }
    throw new InvalidActionException('Type de créneau non reconnu');
  }

  public startNextVacation(): Observable<any> {
    const type = this.getCurrentCreneauOrThrow().type;
    if (type === CreneauType.GARDIENNAGE) {
      return this.startNextVacationGardiennage();
    } else if (type === CreneauType.TOURNEE) {
      return this.startNextVacationTournee();
    }
    throw new InvalidActionException('Type de créneau non supporté ' + type);
  }

  public endCurrentVacationGardiennage(signature: string | null, champsFin?: ChampCompteRendu[]): Observable<any> {
    // 1 update locally
    // 2 send event
    // 3 refresh

    // Après une première fin, `courant` est remis à null puis updateState promeut le créneau suivant.
    // Un second déclenchement rapide (double-clic, ou fin sans créneau restant) tomberait alors sur un
    // créneau null (NPE) ou sur un créneau futur. Ces gardes rendent la fin idempotente et sûre.
    if (!this.tout().courant) {
      console.warn('Aucun créneau courant : fin de gardiennage ignorée');
      return of(this.tout());
    }

    if (!this.getCurrentCreneauOrThrow().vacation) {
      console.warn("Impossible de finir un gardiennage qui n'a pas de vacation");
      // On ne finit pas une vacation qui n'a pas de début
      return of(this.tout());
    }

    // On ne finit jamais un créneau dont le début n'est pas encore arrivé : le serveur le rejette
    // (« le début de la vacation est après la date actuelle ») et c'est le symptôme du ticket #7330.
    // Symétrique du contrôle de date au démarrage (isItTimeToStartCurrentCreneau).
    if (this.getCurrentCreneauOrThrow().debut && dayjs(this.getCurrentCreneauOrThrow().debut).isAfter(dayjs())) {
      console.warn("Impossible de finir un gardiennage dont le début n'est pas encore arrivé", this.getCurrentCreneauOrThrow().id);
      return of(this.tout());
    }

    // le courant à null afin que updateState passe au prochain.
    const previousCreneau = this.getCurrentCreneauOrThrow();
    this.tout().courant = null;
    return this.updateState(this.tout()).pipe(
      switchMap(() => this.getCoordsObsIf(previousCreneau.geolocEnabled ?? true)),
      switchMap(coords =>
        this.userEventService.put(new FinVacationGardiennageEvent(previousCreneau.id, dayjs(), signature ?? undefined, champsFin, coords))
      ),
      switchMap(() => this.refreshCreneau())
    );
  }

  public endCurrentVacationTournee(champsFin?: ChampCompteRendu[]): Observable<any> {
    // 1 update locally
    // 2 send event
    // 3 refresh

    if (!this.getCurrentCreneauOrThrow().vacation) {
      console.warn("Impossible de finir une tournee qui n'a pas de vacation");
      // On ne finit pas une vacation qui n'a pas de début
      return of(this.tout());
    }
    // le courant à null afin que updateState passe au prochain.
    const previousCreneau = this.getCurrentCreneauOrThrow();

    this.tout().courant = null;
    this.log.info('endCurrentVacationTournee', previousCreneau, this.tout());

    return this.updateState(this.tout()).pipe(
      switchMap(() => this.getCoordsObsIf(previousCreneau.geolocEnabled ?? true)),
      switchMap(coords => this.userEventService.put(new FinVacationTourneeEvent(previousCreneau.id, dayjs(), champsFin, coords))),
      switchMap(() => this.refreshCreneau())
    );
  }

  public endCurrentVacationIntervention(signature: string | null, champsFin?: ChampCompteRendu[]): Observable<any> {
    // 1 update locally
    // 2 send event
    // 3 refresh

    const now = dayjs();
    const lastCreneau = this.currentSubCreneau() ?? this.getCurrentCreneauOrThrow();
    if (!lastCreneau.vacation) {
      console.warn("Impossible de finir une intervention qui n'a pas de vacation");
      // On ne finit pas une vacation qui n'a pas de début
      return of(this.tout());
    }

    const idVacationInter = lastCreneau.vacation.id;

    const sousCreneau = this.currentSubCreneau();
    if (sousCreneau) {
      const inter = (this.getCurrentCreneauOrThrow() as CreneauTournee).inters.find(r => r.id === sousCreneau.id);
      if (inter?.vacation) {
        inter.vacation.fin = now;
      }
    } else {
      // le courant à null afin que updateState passe au prochain.
      this.tout().courant = null;
    }
    return this.updateState(this.tout()).pipe(
      switchMap(() => this.getCoordsObsIf(lastCreneau.geolocEnabled ?? true)),
      switchMap(coords =>
        this.userEventService.put(
          new FinVacationInterventionEvent(idVacationInter as string, now, signature ?? undefined, champsFin, coords)
        )
      ),
      switchMap(() => this.refreshCreneau())
    );
  }

  public endCurrentVacationInterPonctuelle(signature: string | null, champsFin?: ChampCompteRendu[]): Observable<any> {
    // 1 update locally
    // 2 send event
    // 3 refresh

    const now = dayjs();
    const lastCreneau = this.getCurrentCreneauOrThrow();
    if (!lastCreneau.vacation) {
      console.warn("Impossible de finir une intervention qui n'a pas de vacation");
      // On ne finit pas une vacation qui n'a pas de début
      return of(this.tout());
    }
    const idAffectation = lastCreneau.id;

    // le courant à null afin que updateState passe au prochain.
    this.tout().courant = null;

    return this.updateState(this.tout()).pipe(
      switchMap(() => this.getCoordsObsIf(lastCreneau.geolocEnabled ?? true)),
      switchMap(coords =>
        this.userEventService.put(new FinVacationInterPonctuelleEvent(idAffectation, now, signature ?? undefined, champsFin, coords))
      ),
      switchMap(() => this.refreshCreneau())
    );
  }

  private updateComputedValues() {
    let deepestOngoingCreneau: ICreneau | null = null;

    this.isStartingInterventionNonPlannifiee.set(!!this.creneauInterventionNonPlannifiee());

    if (this.isStartingInterventionNonPlannifiee()) {
      deepestOngoingCreneau = this.creneauInterventionNonPlannifiee() ?? null;
    } else {
      if (this.tout().courant != null && this.getCurrentCreneauOrThrow().type === CreneauType.TOURNEE) {
        this.currentSubCreneau.set(
          (this.tout().courant as CreneauTournee).inters.find(inter => inter.vacation && !inter.vacation.fin) ?? null
        );
        if (this.currentSubCreneau()) {
          this.chosenNextSubCreneauId.set(null);
        }
      } else {
        this.currentSubCreneau.set(null);
      }

      this.computedHasMainCourante.set(
        (this.tout().courant != null && this.getCurrentCreneauOrThrow().type === CreneauType.GARDIENNAGE) ||
          this.currentSubCreneau()?.type === CreneauType.RONDE ||
          (this.tout().courant != null && this.getCurrentCreneauOrThrow().type === CreneauType.INTERVENTION) ||
          this.currentSubCreneau()?.type === CreneauType.INTERVENTION
      );

      if (this.currentSubCreneau()) {
        deepestOngoingCreneau = this.currentSubCreneau();
      } else if (this.hasCurrentCreneau()) {
        deepestOngoingCreneau = this.tout().courant;
      }
    }

    this.computedHasConsigne.set(
      deepestOngoingCreneau?.consignes != null &&
        deepestOngoingCreneau.consignes.length > 0 &&
        deepestOngoingCreneau.consignes.find(consigne => {
          return dayjs(consigne.debut).isBefore(dayjs()) && (!consigne.fin || dayjs(consigne.fin).isAfter(dayjs()));
        }) !== undefined
    );

    const intersPonctuelles = this.tout().prochains?.filter(creneau => creneau.type === CreneauType.INTER_PONCTUELLE) ?? [];

    this.computedNbInterPonctuelles.set(intersPonctuelles.length);

    this.computedIsCompteRenduRemplis.set(
      (this.tout().courant?.type === CreneauType.INTER_PONCTUELLE &&
        this.tout()
          .courant?.champsPersonnalises.filter(cp => cp.etape === EtapeChampCompteRendu.COMPTE_RENDU)
          .every(cp => cp.isChampCompteRenduFacultatifOuRemplis())) ??
        false
    );
  }

  private getCoordsObsIf(geolocalized: boolean): Observable<Coordinates | undefined> {
    return geolocalized ? this.geolocationService.getCoordinates() : of(undefined);
  }

  public hasInterPonctuelle(): boolean {
    return this.tout().prochains?.some(c => c.type === CreneauType.INTER_PONCTUELLE) ?? false;
  }
  public nbInterPonctuelles(): number {
    return this.computedNbInterPonctuelles();
  }

  public getAllIntersPonctuelles(): CreneauInterPonctuelle[] {
    return (
      this.tout()
        .prochains?.filter(c => c.type === CreneauType.INTER_PONCTUELLE)
        .map(c => c as CreneauInterPonctuelle) ?? []
    );
  }

  public getCurrentInterPonctuelle(): CreneauInterPonctuelle | undefined {
    const currentCreneau = this.getCurrentCreneau();
    return currentCreneau?.type === CreneauType.INTER_PONCTUELLE ? (currentCreneau as CreneauInterPonctuelle) : undefined;
  }

  public getCurrentCreneauClient(): string | undefined {
    if (!this.tout().courant) {
      return;
    } else if (this.getCurrentCreneauOrThrow().type === CreneauType.INTER_PONCTUELLE) {
      const inter: CreneauInterPonctuelle = this.tout().courant as CreneauInterPonctuelle;
      return inter.site?.adresse;
    } else {
      this.log.warn('Type de créneau inconnu', this.getCurrentCreneauOrThrow().type);
    }
  }

  public hasChampsCompteRenduArriveeSurSiteNonRemplit(): boolean {
    if (!this.hasChampsArriveeSurSite()) {
      return false;
    }
    const vacation = (this.currentSubCreneau() ?? this.getCurrentCreneauOrThrow()).vacation as VacationGardiennage | VacationIntervention;
    if (!vacation) {
      return false;
    }
    return !vacation.arriveeSurSite;
  }

  public hasChampsArriveeSurSite(): boolean {
    return (
      (this.currentSubCreneau() ?? this.tout().courant)?.champsPersonnalises.some(
        cp => cp.etape === EtapeChampCompteRendu.ARRIVEE_SUR_SITE
      ) ?? false
    );
  }

  private setIsUpdating(value: boolean) {
    this._isUpdating.set(value);
    this._isUpdating$.next(value);
  }

  public isUpdating(): boolean {
    return this._isUpdating();
  }

  public isUpdating$(): Observable<boolean> {
    return this._isUpdating$.pipe(distinctUntilChanged());
  }

  public setPrecisionLue(id: string): void {
    if (!this.precisionsLues().includes(id)) {
      this.precisionsLues.update(lues => [...lues, id]);
      this.savePrecisionsLues();
    }
  }
}
